import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	faDownload,
	faFilm,
	faFloppyDisk,
	faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { Spinner } from "@/components/Spinner/Spinner";
import { useGetFormatPresetsQuery } from "@/pages/Private/redux/media/media.api";
import { useDownloadExportFileMutation } from "@/pages/Private/redux/media/videoWizard.api";
import { MediaKind, MediaJobType } from "@/pages/Private/redux/media/media.schema";
import {
	DEFAULT_AUDIO_CONFIG,
	SubtitlePosition,
	SubtitleSize,
	SubtitleStyle,
	VIDEO_TRANSITIONS,
	VideoTransition,
	VideoWizard,
	WizardExportConfig,
	WizardTimelineClip,
} from "@/pages/Private/redux/media/videoWizard.schema";
import { classNames } from "@/utils/classNames";

import { Timeline } from "../components/Timeline";
import { TrimEditor } from "../components/TrimEditor";
import { formatSeconds, isStepApproved, isStepLocked } from "../wizard.utils";

interface Step6CutExportProps {
	wizard: VideoWizard;
	isBusy: boolean;
	onSaveTimeline: (clips: WizardTimelineClip[]) => Promise<unknown>;
	onPatchExport: (exportConfig: WizardExportConfig) => void;
	onExport: () => void;
}

/** How long after the last edit the cut is written back. Long enough to survive a drag. */
const SAVE_DEBOUNCE_MS = 600;

/**
 * Step 5 — the cut, and the video it becomes.
 *
 * Two editors over one state: a strip of clips that can be reordered, and a trim editor for
 * whichever of them is selected. Both write `in`/`out` in tenths of a second with a
 * two-second minimum, and neither decides anything — the server enforces every one of those
 * rules again and is what actually holds the timeline.
 *
 * The one call in the wizard that is deliberately not fired per interaction. Every mutation
 * bumps the session's `version` and the wizard refetches on every socket push, so a write
 * per drag frame would race itself into `STALE_VERSION`. Instead the cut is held locally,
 * written back {@link SAVE_DEBOUNCE_MS} after an interaction settles, and flushed before the
 * export — with an explicit save button so "not yet written" is never a silent state.
 */
export const Step6CutExport: FunctionComponent<Step6CutExportProps> = ({
	wizard,
	isBusy,
	onSaveTimeline,
	onPatchExport,
	onExport,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) =>
			t(`videoWizard.steps.cutExport.${key}`, options ?? {}),
		[t]
	);

	const { data: presets = [] } = useGetFormatPresetsQuery({ mediaKind: MediaKind.VIDEO });

	const locked = isStepLocked(wizard, 6);
	const approved = isStepApproved(wizard, 6);

	// The cut being edited. Seeded from the server and re-seeded whenever the server's own
	// version of it changes — a regenerate upstream rewrites the timeline wholesale.
	const [clips, setClips] = useState<WizardTimelineClip[]>(wizard.timeline);
	const [selected, setSelected] = useState(0);
	const [isDirty, setIsDirty] = useState(false);
	const [isDownloadOpen, setDownloadOpen] = useState(false);
	// Which rendered shape the finished player shows — every format is reachable here,
	// not only the one the intro link happens to point at.
	const [playingFormatId, setPlayingFormatId] = useState<number | null>(null);
	const [downloadExport] = useDownloadExportFileMutation();
	const downloadMenuRef = useRef<HTMLDivElement>(null);

	// The menu closes like any menu: choosing an entry, or clicking anywhere else.
	useEffect(() => {
		if (!isDownloadOpen) {
			return undefined;
		}

		const onPointerDown = (event: MouseEvent) => {
			if (!downloadMenuRef.current?.contains(event.target as Node)) {
				setDownloadOpen(false);
			}
		};

		document.addEventListener("mousedown", onPointerDown);

		return () => document.removeEventListener("mousedown", onPointerDown);
	}, [isDownloadOpen]);

	// Fetched with the session's authorization and saved as a file — the pattern the
	// scene-image download established.
	const handleDownload = async (choice: { formatPresetId?: number; subtitle?: boolean }) => {
		setDownloadOpen(false);

		try {
			const { blob, filename } = await downloadExport({ id: wizard.id, ...choice }).unwrap();
			const url = URL.createObjectURL(blob);
			const anchor = document.createElement("a");

			anchor.href = url;
			anchor.download = filename;
			anchor.click();
			URL.revokeObjectURL(url);
		} catch {
			// The section keeps showing the files; there is nothing to roll back.
		}
	};
	const serverTimeline = useMemo(() => JSON.stringify(wizard.timeline), [wizard.timeline]);
	const savedTimeline = useRef(serverTimeline);

	useEffect(() => {
		// Only when the server's timeline actually differs from the one this pane last saw.
		// Without the guard, every refetch would throw away the edit in progress.
		if (savedTimeline.current !== serverTimeline) {
			savedTimeline.current = serverTimeline;
			setClips(wizard.timeline);
			setIsDirty(false);
		}
	}, [serverTimeline, wizard.timeline]);

	const scenesById = useMemo(
		() =>
			new Map(
				wizard.storyboard.scenes.map((scene, index) => [scene.id, { scene, number: index + 1 }])
			),
		[wizard.storyboard.scenes]
	);

	const save = useCallback(
		async (next: WizardTimelineClip[]) => {
			savedTimeline.current = JSON.stringify(next);
			setIsDirty(false);
			await onSaveTimeline(next);
		},
		[onSaveTimeline]
	);

	// The debounce. Deliberately keyed on `isDirty` rather than on the clips themselves, so
	// a re-render caused by anything else does not restart the timer.
	useEffect(() => {
		if (!isDirty) {
			return undefined;
		}

		const timer = window.setTimeout(() => void save(clips), SAVE_DEBOUNCE_MS);

		return () => window.clearTimeout(timer);
	}, [isDirty, clips, save]);

	const totalSeconds = useMemo(
		() =>
			Math.round(
				clips.reduce((total, clip) => total + (clip.outSeconds - clip.inSeconds), 0) * 10
			) / 10,
		[clips]
	);

	const exportJob = wizard.activeJobs.find((job) => job.type === MediaJobType.VIDEO_EXPORT);
	const chosenFormatIds =
		wizard.exportConfig?.formatPresetIds ?? presets.map((preset) => preset.id);
	const transition = wizard.exportConfig?.transition ?? VideoTransition.HARD_CUT;
	// Absent means the intro, which is what every session did before the choice existed.
	const useAsIntroVideo = wizard.exportConfig?.useAsIntroVideo !== false;

	if (locked) {
		return (
			<div className="space-y-4">
				<header>
					<h2 className="text-lg font-bold text-ink">{ts("heading")}</h2>
				</header>
				<div className="rounded-card border border-dashed border-border bg-surface px-4 py-10 text-center text-sm text-subtle">
					{ts("locked")}
				</div>
			</div>
		);
	}

	const reorder = (from: number, to: number) => {
		setClips((current) => {
			const next = [...current];
			const [moved] = next.splice(from, 1);

			next.splice(to, 0, moved);

			return next.map((clip, position) => ({ ...clip, position }));
		});
		setSelected(to);
		setIsDirty(true);
	};

	const selectedClip = clips[Math.min(selected, Math.max(0, clips.length - 1))];
	const selectedEntry = selectedClip ? scenesById.get(selectedClip.storyboardSceneId) : undefined;

	return (
		<div className="space-y-4">
			<header className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<h2 className="text-lg font-bold text-ink">{ts("heading")}</h2>
					<p className="mt-1 max-w-[640px] text-xs text-subtle">{ts("description")}</p>
				</div>
				<span
					className={classNames(
						"shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold",
						approved
							? "bg-success-light text-text"
							: exportJob
								? "bg-status-running-light text-warning-ink"
								: "bg-surface text-subtle"
					)}
				>
					{approved
						? ts("exportedBadge")
						: exportJob
							? ts("renderingBadge")
							: ts("awaitingApprovalBadge")}
				</span>
			</header>

			{/* No model row here: which render pipeline runs is registry plumbing, not an
			    authoring decision — too technical for the person cutting a video. */}

			{/* The finished video, once there is one. */}
			{!!wizard.introCourseFileUrl && (
				<section className="space-y-3 rounded-card border border-border-soft bg-surface p-4">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<span className="text-sm font-bold text-ink">{ts("finishedHeading")}</span>
						{/* The badge states the fact, not the setting: linked as the intro, or
						    kept in the media library. */}
						<span className="rounded-full bg-success-light px-2.5 py-1 text-xs font-semibold text-text">
							{ts(wizard.introLinked ? "linkedBadge" : "libraryBadge")}
						</span>
					</div>

					{/* The subtitles the author chose are burnt into this file, so the player
					    carries no <track>: a sidecar track exists exactly when the burn does,
					    and browsers auto-enable a track for anyone whose caption preference is
					    on — which put the same words on screen twice, once from the pixels and
					    once from the VTT. The sidecar stays a download below, for players and
					    platforms that want a separate file. */}
					{(wizard.exportAssets?.length ?? 0) > 1 && (
						<div className="flex flex-wrap gap-1.5">
							{(wizard.exportAssets ?? []).map((asset) => (
								<button
									key={asset.formatPresetId}
									className={classNames(
										"rounded-full border px-2.5 py-1 text-xs font-bold transition-colors",
										(playingFormatId ?? wizard.exportAssets?.[0]?.formatPresetId) ===
											asset.formatPresetId
											? "border-accent bg-accent-tint text-text"
											: "border-border bg-white text-subtle hover:border-accent"
									)}
									type="button"
									onClick={() => setPlayingFormatId(asset.formatPresetId)}
								>
									{ts("formatChip", {
										label: asset.label,
										width: asset.width,
										height: asset.height,
									})}
								</button>
							))}
						</div>
					)}

					<video
						controls
						className="video-own-size mx-auto max-h-[420px] w-auto max-w-full rounded-card bg-ink"
						preload="metadata"
						src={
							(wizard.exportAssets ?? []).find(
								(asset) =>
									asset.formatPresetId ===
									(playingFormatId ?? wizard.exportAssets?.[0]?.formatPresetId)
							)?.url ?? wizard.introCourseFileUrl
						}
					/>

					{/* One download button, the choices in its menu — a real save-as, streamed
					    through the API, not a link that opens the bucket in a new tab. */}
					<div ref={downloadMenuRef} className="relative w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2" icon={faDownload} />}
							size={ButtonSize.S}
							title={ts("downloadButton")}
							type="button"
							onClick={() => setDownloadOpen((open) => !open)}
						/>
						{isDownloadOpen && (
							<div className="absolute left-0 top-full z-20 mt-1 w-64 rounded-card border border-border bg-white p-1 shadow-custom-xs">
								{(wizard.exportAssets ?? []).map((asset) => (
									<button
										key={asset.formatPresetId}
										className="block w-full rounded px-3 py-2 text-left text-sm font-semibold text-ink hover:bg-surface"
										type="button"
										onClick={() => handleDownload({ formatPresetId: asset.formatPresetId })}
									>
										{ts("formatChip", {
											label: asset.label,
											width: asset.width,
											height: asset.height,
										})}
									</button>
								))}
								{!!wizard.exportSubtitleUrl && (
									<button
										className="block w-full rounded px-3 py-2 text-left text-sm font-semibold text-ink hover:bg-surface"
										type="button"
										onClick={() => handleDownload({ subtitle: true })}
									>
										{ts("subtitleDownload")}
									</button>
								)}
							</div>
						)}
					</div>

					{!!wizard.exportWarnings?.length && (
						<ul className="space-y-1.5" role="list">
							{wizard.exportWarnings.map((warning) => (
								<li
									key={`${warning.code}-${warning.storyboardSceneId ?? "all"}`}
									className="flex gap-2 rounded-card bg-status-running-light px-3 py-2 text-xs text-warning-ink"
								>
									<Icon icon={faTriangleExclamation} />
									{/* Phrased from the code in the UI's language — but only when the
									    warning carries its structured details: a warning from before
									    they existed would render the template's bare placeholders.
									    The server's prose stands in for those and for unknown codes. */}
									<span>
										{warning.details
											? ts(`warningTexts.${warning.code}`, {
													...warning.details,
													defaultValue: warning.message,
												})
											: warning.message}
									</span>
								</li>
							))}
						</ul>
					)}
				</section>
			)}

			{/* The cut. */}
			<section className="space-y-3 rounded-card border border-border-soft bg-surface p-4">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<span className="text-sm font-bold text-ink">{ts("timelineHeading")}</span>
					<div className="flex flex-wrap items-center gap-2">
						{isDirty && (
							<span className="rounded-full bg-status-running-light px-2.5 py-1 text-xs font-semibold text-warning-ink">
								{ts("unsaved")}
							</span>
						)}
						<span
							className={classNames(
								"rounded-full px-2.5 py-1 text-xs font-semibold",
								totalSeconds === wizard.targetLengthSeconds
									? "bg-success-light text-text"
									: "bg-accent-tint text-ink"
							)}
						>
							{ts("total", {
								total: formatSeconds(totalSeconds),
								target: wizard.targetLengthSeconds,
							})}
						</span>
					</div>
				</div>
				<p className="text-xs text-subtle">{ts("timelineHint")}</p>

				<Timeline
					clips={clips}
					disabled={isBusy || !!exportJob}
					scenesById={scenesById}
					selectedPosition={selected}
					onMove={reorder}
					onSelect={setSelected}
				/>

				{isDirty && (
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={isBusy}
						image={<Icon className="mr-2" icon={faFloppyDisk} />}
						size={ButtonSize.S}
						title={ts("saveNow")}
						type="button"
						onClick={() => void save(clips)}
					/>
				)}
			</section>

			{!!selectedClip && !!selectedEntry && (
				<TrimEditor
					clip={selectedClip}
					disabled={isBusy || !!exportJob}
					scene={selectedEntry.scene}
					sceneNumber={selectedEntry.number}
					// The preview's live captions render exactly the choice the export will
					// burn — style, position and size, defaults resolved the same way.
					subtitles={{
						style:
							(wizard.audioConfig?.subtitleStyle as SubtitleStyle) ??
							DEFAULT_AUDIO_CONFIG.subtitleStyle,
						position:
							(wizard.audioConfig?.subtitlePosition as SubtitlePosition) ??
							DEFAULT_AUDIO_CONFIG.subtitlePosition,
						size:
							(wizard.audioConfig?.subtitleSize as SubtitleSize) ??
							DEFAULT_AUDIO_CONFIG.subtitleSize,
					}}
					onChange={(next) => {
						setClips((current) =>
							current.map((clip, position) => (position === selected ? { ...clip, ...next } : clip))
						);
						setIsDirty(true);
					}}
					onCommit={() => setIsDirty(true)}
				/>
			)}

			{/* The export itself. */}
			<section className="space-y-3 rounded-card border border-border-soft bg-surface p-4">
				<span className="text-sm font-bold text-ink">{ts("exportHeading")}</span>

				<div className="flex flex-wrap gap-4">
					<div className="min-w-[220px]">
						<span className="mb-1.5 block text-xs font-bold uppercase text-subtle">
							{ts("transitionLabel")}
						</span>
						<div className="flex flex-wrap gap-2">
							{VIDEO_TRANSITIONS.map((option) => (
								<button
									key={option}
									aria-pressed={transition === option}
									className={classNames(
										"rounded-full border px-3 py-1.5 text-xs font-semibold",
										transition === option
											? "border-accent bg-accent-tint text-ink"
											: "border-border-soft bg-white text-subtle"
									)}
									disabled={isBusy || !!exportJob}
									type="button"
									onClick={() => onPatchExport({ transition: option })}
								>
									{ts(`transitions.${option}`)}
								</button>
							))}
						</div>
					</div>

					<div className="min-w-[240px] flex-1">
						<span className="mb-1.5 block text-xs font-bold uppercase text-subtle">
							{ts("formatsLabel")}
						</span>
						<div className="flex flex-wrap gap-2">
							{presets.map((preset) => {
								const isChosen = chosenFormatIds.includes(preset.id);

								return (
									<button
										key={preset.id}
										aria-pressed={isChosen}
										className={classNames(
											"rounded-full border px-3 py-1.5 text-xs font-semibold",
											isChosen
												? "border-accent bg-accent-tint text-ink"
												: "border-border-soft bg-white text-subtle"
										)}
										disabled={isBusy || !!exportJob}
										type="button"
										onClick={() => {
											const next = isChosen
												? chosenFormatIds.filter((id) => id !== preset.id)
												: [...chosenFormatIds, preset.id];

											// One format at least: an export of nothing is not an export.
											if (next.length) {
												onPatchExport({ formatPresetIds: next });
											}
										}}
									>
										{ts("formatChip", {
											label: preset.label,
											width: preset.width,
											height: preset.height,
										})}
									</button>
								);
							})}
						</div>
						<p className="mt-1.5 text-xs text-subtle">{ts("formatsHint")}</p>
					</div>
				</div>

				{/* What the finished video is for. Separate from the render settings above,
				    because it changes nothing about the pixels — and changing it therefore
				    does not invalidate a finished export. */}
				<div className="min-w-[240px]">
					<span className="mb-1.5 block text-xs font-bold uppercase text-subtle">
						{ts("destinationLabel")}
					</span>
					<div className="flex flex-wrap gap-2">
						{[true, false].map((asIntro) => (
							<button
								key={String(asIntro)}
								aria-pressed={useAsIntroVideo === asIntro}
								className={classNames(
									"rounded-full border px-3 py-1.5 text-xs font-semibold",
									useAsIntroVideo === asIntro
										? "border-accent bg-accent-tint text-ink"
										: "border-border-soft bg-white text-subtle"
								)}
								disabled={isBusy || !!exportJob}
								type="button"
								onClick={() => onPatchExport({ useAsIntroVideo: asIntro })}
							>
								{ts(asIntro ? "destinationIntro" : "destinationLibrary")}
							</button>
						))}
					</div>
					<p className="mt-1.5 text-xs text-subtle">
						{ts(useAsIntroVideo ? "destinationIntroHint" : "destinationLibraryHint")}
					</p>
				</div>

				<div className="flex flex-wrap items-center gap-3">
					<Button
						color={ButtonColor.ACTION}
						disabled={isBusy || !!exportJob || !clips.length}
						image={exportJob ? <Spinner /> : <Icon icon={faFilm} />}
						size={ButtonSize.M}
						title={exportJob ? ts("exporting") : ts(approved ? "exportAgain" : "export", {})}
						type="button"
						onClick={async () => {
							// The cut is written first, so the render is of what is on screen
							// rather than of whatever the last debounce happened to catch.
							if (isDirty) {
								await save(clips);
							}

							onExport();
						}}
					/>
					<p className="text-xs text-subtle">{ts("exportHint")}</p>
				</div>
			</section>
		</div>
	);
};
